import React, { useEffect, useState, useRef, useCallback } from 'react';
import { adminApi } from '../../lib/api';
import { 
  Package, Car, Route, DollarSign, List, TrendingUp, TrendingDown, 
  Wallet, Clock, CheckCircle, XCircle, Ban, ArrowUpRight, ArrowDownRight,
  BarChart3, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// Hook for animated counter
const useAnimatedCounter = (endValue, duration = 1200, decimals = 0) => {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    if (endValue === 0 && value === 0) return;
    startTimeRef.current = null;

    const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

    const animate = (timestamp) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutExpo(progress);
      const currentValue = easedProgress * endValue;
      setValue(currentValue);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setValue(endValue);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [endValue, duration]);

  if (decimals > 0) return value.toFixed(decimals);
  return Math.round(value);
};

const AnimatedNumber = ({ value, prefix = '', suffix = '', decimals = 0, duration = 1200 }) => {
  const animated = useAnimatedCounter(value, duration, decimals);
  return <>{prefix}{typeof animated === 'number' ? animated.toLocaleString() : Number(animated).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</>;
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  paid: 'Pagada',
  confirmed: 'Confirmada',
  rejected: 'Rechazada',
  cancelled: 'Cancelada',
};

export const AdminDashboard = () => {
  const [reservations, setReservations] = useState([]);
  const [stats, setStats] = useState({
    total: 0, vehicles: 0, routes: 0, addons: 0
  });
  const [finance, setFinance] = useState({
    revenue: 0, debited: 0, net: 0, pending: 0,
    countConfirmed: 0, countCancelled: 0, countRejected: 0, countPaid: 0, countPending: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [resRes, vehRes, routeRes, addonRes] = await Promise.all([
        adminApi.getReservations(1, 100),
        adminApi.getVehicles(1, 100),
        adminApi.getRoutes(1, 100),
        adminApi.getAddons(1, 100)
      ]);

      const all = resRes.data.items || [];
      setReservations(all);
      setStats({
        total: resRes.data.total || all.length,
        vehicles: vehRes.data.total || 0,
        routes: routeRes.data.total || 0,
        addons: addonRes.data.total || 0
      });

      // Financial calculations
      let revenue = 0, debited = 0, pending = 0;
      let countConfirmed = 0, countCancelled = 0, countRejected = 0, countPaid = 0, countPending = 0;

      all.forEach(r => {
        const t = r.total || 0;
        switch (r.status) {
          case 'confirmed':
            revenue += t;
            countConfirmed++;
            break;
          case 'cancelled':
            debited += t;
            countCancelled++;
            break;
          case 'rejected':
            debited += t;
            countRejected++;
            break;
          case 'paid':
            pending += t;
            countPaid++;
            break;
          case 'pending':
            countPending++;
            break;
          default: break;
        }
      });

      setFinance({
        revenue, debited, net: revenue - debited, pending,
        countConfirmed, countCancelled, countRejected, countPaid, countPending
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const recentReservations = reservations.slice(0, 7);

  return (
    <div className="space-y-6">
      <h1 className="font-manrope font-bold text-3xl text-emerald-950">Dashboard</h1>

      {/* Financial Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Revenue */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50 rounded-bl-[4rem] -mr-2 -mt-2"></div>
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <span className="text-sm font-medium text-slate-500">Ingresos</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 tracking-tight">
              <AnimatedNumber value={finance.revenue} prefix="$" decimals={2} duration={1400} />
            </p>
            <p className="text-xs text-emerald-600 font-medium mt-1 flex items-center gap-1">
              <ArrowUpRight className="w-3.5 h-3.5" />
              {finance.countConfirmed} reservas confirmadas
            </p>
          </div>
        </div>

        {/* Debited */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-red-50 rounded-bl-[4rem] -mr-2 -mt-2"></div>
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-red-600" />
              </div>
              <span className="text-sm font-medium text-slate-500">Cancelado / Rechazado</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 tracking-tight">
              <AnimatedNumber value={finance.debited} prefix="$" decimals={2} duration={1400} />
            </p>
            <p className="text-xs text-red-500 font-medium mt-1 flex items-center gap-1">
              <ArrowDownRight className="w-3.5 h-3.5" />
              {finance.countCancelled + finance.countRejected} reservas
            </p>
          </div>
        </div>

        {/* Net */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 relative overflow-hidden">
          <div className={`absolute top-0 right-0 w-24 h-24 rounded-bl-[4rem] -mr-2 -mt-2 ${finance.net >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}></div>
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${finance.net >= 0 ? 'bg-emerald-100' : 'bg-red-100'}`}>
                <Wallet className={`w-5 h-5 ${finance.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
              </div>
              <span className="text-sm font-medium text-slate-500">Balance Neto</span>
            </div>
            <p className={`text-3xl font-bold tracking-tight ${finance.net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
              <AnimatedNumber value={finance.net} prefix="$" decimals={2} duration={1600} />
            </p>
            <p className="text-xs text-slate-500 font-medium mt-1">
              Ingresos - Cancelaciones
            </p>
          </div>
        </div>

        {/* Pending */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-[4rem] -mr-2 -mt-2"></div>
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-slate-500">Por Aprobar</span>
            </div>
            <p className="text-3xl font-bold text-slate-900 tracking-tight">
              <AnimatedNumber value={finance.pending} prefix="$" decimals={2} duration={1400} />
            </p>
            <p className="text-xs text-blue-600 font-medium mt-1 flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {finance.countPaid} reservas pagadas
            </p>
          </div>
        </div>
      </div>

      {/* Resource Counts + Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Resources */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <h2 className="font-manrope font-semibold text-base text-slate-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-slate-400" />
            Recursos
          </h2>
          <div className="space-y-3">
            {[
              { label: 'Reservas totales', value: stats.total, icon: List, color: 'text-blue-600 bg-blue-50' },
              { label: 'Vehiculos activos', value: stats.vehicles, icon: Car, color: 'text-emerald-600 bg-emerald-50' },
              { label: 'Rutas disponibles', value: stats.routes, icon: Route, color: 'text-purple-600 bg-purple-50' },
              { label: 'Extras/Addons', value: stats.addons, icon: Package, color: 'text-orange-600 bg-orange-50' },
            ].map(item => {
              const Icon = item.icon;
              return (
                <div key={item.label} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-sm text-slate-600">{item.label}</span>
                  </div>
                  <span className="text-lg font-bold text-slate-900">
                    <AnimatedNumber value={item.value} duration={1000} />
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Status Breakdown */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <h2 className="font-manrope font-semibold text-base text-slate-800 mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-400" />
            Estado de Reservas
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: 'Pendientes', count: finance.countPending, icon: Clock, color: 'bg-gray-100 text-gray-700', accent: 'bg-gray-400' },
              { label: 'Pagadas', count: finance.countPaid, icon: DollarSign, color: 'bg-blue-100 text-blue-700', accent: 'bg-blue-500' },
              { label: 'Confirmadas', count: finance.countConfirmed, icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700', accent: 'bg-emerald-500' },
              { label: 'Rechazadas', count: finance.countRejected, icon: XCircle, color: 'bg-red-100 text-red-700', accent: 'bg-red-500' },
              { label: 'Canceladas', count: finance.countCancelled, icon: Ban, color: 'bg-orange-100 text-orange-700', accent: 'bg-orange-500' },
            ].map(item => {
              const Icon = item.icon;
              const pct = stats.total > 0 ? (item.count / stats.total) * 100 : 0;
              return (
                <button
                  key={item.label}
                  onClick={() => navigate('/admin/reservations')}
                  className="text-left p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <Icon className={`w-4 h-4 ${item.color.split(' ')[1]}`} />
                    <span className="text-xs font-medium text-slate-500 group-hover:text-slate-700 transition-colors">{item.label}</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900 mb-2">
                    <AnimatedNumber value={item.count} duration={900} />
                  </p>
                  <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${item.accent} transition-all duration-1000 ease-out`}
                      style={{ width: `${pct}%` }}
                    ></div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Reservations */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-manrope font-semibold text-base text-slate-800">Reservas Recientes</h2>
          <button
            onClick={() => navigate('/admin/reservations')}
            className="text-sm text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
          >
            Ver todas
          </button>
        </div>

        {recentReservations.length === 0 ? (
          <p className="text-slate-500 text-center py-8">No hay reservas aun</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">ID</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Cliente</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Ruta</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Fecha</th>
                  <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                  <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Estado</th>
                </tr>
              </thead>
              <tbody>
                {recentReservations.map((reservation) => {
                  const statusStyles = {
                    confirmed: 'bg-emerald-100 text-emerald-700',
                    paid: 'bg-blue-100 text-blue-700',
                    pending: 'bg-gray-100 text-gray-700',
                    rejected: 'bg-red-100 text-red-700',
                    cancelled: 'bg-orange-100 text-orange-700',
                  };
                  return (
                    <tr key={reservation.reservation_id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <td className="py-3 px-4 text-sm font-mono text-slate-600">{reservation.reservation_id.substring(0, 8)}</td>
                      <td className="py-3 px-4 text-sm font-medium text-slate-800">{reservation.customer.full_name}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{reservation.from_label} &rarr; {reservation.to_label}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {new Date(reservation.pickup_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="py-3 px-4 text-sm font-semibold text-right text-slate-900">${Number(reservation.total).toFixed(2)}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full ${statusStyles[reservation.status] || 'bg-slate-100 text-slate-700'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            reservation.status === 'confirmed' ? 'bg-emerald-500' :
                            reservation.status === 'paid' ? 'bg-blue-500' :
                            reservation.status === 'rejected' ? 'bg-red-500' :
                            reservation.status === 'cancelled' ? 'bg-orange-500' :
                            'bg-gray-400'
                          }`}></span>
                          {STATUS_LABELS[reservation.status] || reservation.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
